import api from './baseApi';
import type {
    TemplateProduct,
    TemplateProductHomeDto,
    TemplateProductVariantDto,
    TemplateProductsListResult
} from './typings/templateProducts';
import type {
    ProductGuideSpecs,
    SpecsTemplateProductDTO,
    TemplateSpecDetailDTO
} from '@/typings/pod/productGuideSpecs';

export const saveTemplateProduct = async (
    templateProduct: TemplateProduct,
    id?: string,
    useListEPS = true
): Promise<TemplateProduct> => {
    const url = id ? `/TemplateProducts/${id}` : '/TemplateProducts';
    const response = await api.put(url, templateProduct, { params: { useListEPS } });
    return response.data;
};
export interface ResultListDTO<T = unknown> {
    results: T[];
    totalCount: number;
}

export interface ProviderTemplateProductDTO {
    id: string;
    name: string;
    thumbnailPath: string;
    linked: boolean;
    productId: number;
    podId: number;
    pod: string;
    apiKeyReference: string;
    podProductId?: string;
    templateProductId?: number;
}

export const listTemplateProducts = async (
    from: number,
    count: number,
    search?: string
): Promise<ResultListDTO<ProviderTemplateProductDTO>> =>
    api
        .get<ResultListDTO<ProviderTemplateProductDTO>>('/TemplateProducts/ListTemplateProducts', {
            params: { from, count, search }
        })
        .then(({ data }) => data);

export const listTemplateProductSpecs = async (
    from: number,
    count: number,
    filterByGuideLines: boolean,
    search?: string
): Promise<ResultListDTO<SpecsTemplateProductDTO>> =>
    api
        .get<ResultListDTO<SpecsTemplateProductDTO>>('/TemplateProducts/ListTemplateProductSpecs', {
            params: { from, count, search, filterByGuideLines }
        })
        .then(({ data }) => data);

export const getSpecDetails = async (templateProductId: string): Promise<TemplateSpecDetailDTO[]> =>
    api.get<TemplateSpecDetailDTO[]>(`/TemplateProducts/SpecDetails/${templateProductId}`).then(({ data }) => data);

export const getGuideSpecs = async (templateId: string, epsId?: number): Promise<ProductGuideSpecs> => {
    const response = await api.get(`/TemplateProducts/${templateId}/GetGuideSpecs`, { params: { epsId } });
    return response.data;
};
export const listMyProductBases = async (
    from: number,
    count: number,
    search?: string,
    sortBy?: string,
    sortMode?: string
): Promise<TemplateProductsListResult> => {
    const response = await api.get('/TemplateProducts/ListMyProductBases', {
        params: {
            from,
            count,
            sortBy,
            sortMode,
            search: search?.toLocaleLowerCase()
        }
    });
    return response.data;
};

export const getTemplates = async (from: number, to: number, search?: string, sortBy?: string, sortMode?: string) => {
    const templates = api.get<TemplateProductHomeDto[]>('/Product/GetProductsFromTo', {
        params: {
            from,
            to,
            sortBy,
            sortMode,
            searchString: search?.toLocaleLowerCase()
        }
    });
    const templatesCount = api.get<number>('/Product/GetProductsCount', {
        params: {
            from,
            to,
            sortBy,
            sortMode,
            searchString: search?.toLocaleLowerCase()
        }
    });

    await Promise.all([templates, templatesCount]);

    return {
        templateProducts: await templates.then((response) => response.data),
        totalCount: await templatesCount.then((response) => response.data)
    };
};

export const getTemplateProduct = async (id: string, isChildTemplateProduct = false): Promise<TemplateProduct> => {
    const includeVariants = !isChildTemplateProduct;
    const response = await api.get(`/TemplateProducts/${id}/${includeVariants}`);
    return response.data;
};

export const getTemplateProductVariantOptions = async (id: string): Promise<TemplateProductVariantDto[]> => {
    const response = await api.get(`/TemplateProductVariants/${id}/OptionsFormat`);
    return response.data;
};

export const getTemplateProductIdsByProviderProductIdAndVariantOptions = async (
    providerProductId: string,
    placeholderId: string,
    variantOptionsChosen: { [key: string]: string[] }
): Promise<string[]> => {
    const response = await api.get(`/TemplateProducts/${providerProductId}/Matched`, {
        params: {
            variantOptionsChosen: encodeURIComponent(JSON.stringify(variantOptionsChosen)),
            placeholderId
        }
    });
    return response.data;
};

export const getProductBase = async (id: string, isChildTemplateProduct = false): Promise<TemplateProduct> => {
    const includeVariants = !isChildTemplateProduct;
    const response = await api.get(`/TemplateProducts/${id}/${includeVariants}`, { params: { useListEPS: true } });
    return response.data;
};

export const cloneProductBase = async (id: string): Promise<TemplateProduct> => {
    const response = await api.post(`/TemplateProducts/${id}/Clone`);
    return response.data;
};

export const deleteProductBase = async (id: string): Promise<TemplateProduct> => {
    const response = await api.delete(`/TemplateProducts/${id}`);
    return response.data;
};

export const renameProductBase = (id: string, name: string) =>
    api.put(`/TemplateProducts/Rename/${id}`, null, { params: { name } });

export const updateTemplateProductThumb = (id: string, path: string) =>
    api.put(`/TemplateProducts/UpdateThumb/${id}`, null, { params: { path } });
